import { safe } from "@orpc/client";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import z from "zod";
import { ComingSoon } from "@/components/coming-soon";
import { useAppForm } from "@/components/form";
import {
	Field,
	FieldContent,
	FieldDescription,
	FieldGroup,
	FieldLabel,
	FieldTitle,
} from "@/components/ui/field";
import {
	FormCard,
	FormCardContent,
	FormCardHeader,
	FormCardTitle,
} from "@/components/ui/form-card";
import { Switch } from "@/components/ui/switch";
import { ChannelStatus } from "@/enums/channel-status";
import { EpisodeStatus } from "@/enums/episode-status";
import {
	isMidRollEnabled,
	isPostRollEnabled,
	isPreRollEnabled,
	MonetizationStatus,
	toggleMonetizationStatus,
} from "@/enums/monetization-status";
import { orpc, validationErrors } from "@/orpc/client";

export const Route = createFileRoute("/_app/episodes/$episodeId/monetization")({
	component: Page,
	params: z.object({
		episodeId: z.coerce.number(),
	}),
	async loader({ context, params, preload }) {
		if (!preload) {
			return;
		}

		context.queryClient.prefetchQuery(
			orpc.episodes.show.queryOptions({
				input: {
					episodeId: params.episodeId,
				},
			}),
		);
	},
});

function Page() {
	const params = Route.useParams();

	const { data: episode } = useSuspenseQuery(
		orpc.episodes.show.queryOptions({
			input: {
				episodeId: params.episodeId,
			},
		}),
	);

	const updateMutation = useMutation(
		orpc.episodes.updateMonetization.mutationOptions({
			onSuccess: (_data, _payload, _result, context) => {
				context.client.invalidateQueries({ queryKey: orpc.episodes.key() });
				toast.success("Episode monetization updated successfully.");
			},
			onError: () => {
				toast.error("Failed to update this episode monetization.");
			},
		}),
	);

	const form = useAppForm({
		defaultValues: {
			MonetizationStatusID: episode.MonetizationStatusID,
			IntroAdJingleID: episode.IntroAdJingleID || null,
			ExtroAdJingleID: episode.ExtroAdJingleID || null,
			IntroJingleID: episode.IntroJingleID || null,
			ExtroJingleID: episode.ExtroJingleID || null,
		},
		canSubmitWhenInvalid: true,
		validators: {
			onSubmitAsync: async ({ value }) => {
				const { error } = await safe(
					updateMutation.mutateAsync({
						...value,
						ID: episode.ID,
					}),
				);
				if (error) {
					return validationErrors(error) ?? "An error occurred while updating the channel.";
				}
			},
		},
	});

	const isEpisodeEditable =
		![EpisodeStatus.Removed].includes(episode.StatusID) &&
		![ChannelStatus.Removed, ChannelStatus.Removed_TS].includes(episode.Channel.StatusID);

	return (
		<form.AppForm>
			<form
				className="relative"
				id={form.formId}
				onSubmit={(e) => {
					e.preventDefault();
					e.stopPropagation();
					form.handleSubmit();
				}}
			>
				<div className="grid grid-cols-12 gap-6 px-4 lg:px-6">
					<FormCard
						className="col-span-12"
						disabled={!isEpisodeEditable || updateMutation.isPending}
					>
						<FormCardHeader>
							<FormCardTitle>Episode Monetization</FormCardTitle>
						</FormCardHeader>
						<FormCardContent>
							<form.AppField
								name="MonetizationStatusID"
								children={(field) => (
									<FieldGroup className="gap-2">
										<FieldLabel className="col-span-12">
											<Field orientation="horizontal">
												<FieldContent>
													<FieldTitle>Monetization</FieldTitle>
													<FieldDescription>
														Enable the monetization for this episode
													</FieldDescription>
												</FieldContent>
												<Switch
													disabled={!isEpisodeEditable}
													checked={
														(field.state.value ?? MonetizationStatus.Undefined) >
														MonetizationStatus.Undefined
													}
													onCheckedChange={(checked) =>
														field.handleChange(
															checked
																? MonetizationStatus.PrerollMidrollPostroll
																: MonetizationStatus.Deactivated,
														)
													}
												/>
											</Field>
										</FieldLabel>
										<FieldLabel className="col-span-12">
											<Field orientation="horizontal">
												<FieldContent>
													<FieldTitle>Pre-roll Ads</FieldTitle>
													<FieldDescription>
														Pre-roll ads are played before the episode starts.
													</FieldDescription>
												</FieldContent>
												<Switch
													disabled={!isEpisodeEditable}
													checked={isPreRollEnabled(
														field.state.value ?? MonetizationStatus.Undefined,
													)}
													onCheckedChange={() =>
														field.handleChange(
															toggleMonetizationStatus(
																field.state.value ?? MonetizationStatus.Undefined,
																"preroll",
															),
														)
													}
												/>
											</Field>
										</FieldLabel>
										<FieldLabel className="col-span-12">
											<Field orientation="horizontal">
												<FieldContent>
													<FieldTitle>Mid-roll Ads</FieldTitle>
													<FieldDescription>
														Mid-roll ads are played by interrupting the episode playback.
													</FieldDescription>
												</FieldContent>
												<Switch
													disabled={!isEpisodeEditable}
													checked={isMidRollEnabled(
														field.state.value ?? MonetizationStatus.Undefined,
													)}
													onCheckedChange={() =>
														field.handleChange(
															toggleMonetizationStatus(
																field.state.value ?? MonetizationStatus.Undefined,
																"midroll",
															),
														)
													}
												/>
											</Field>
										</FieldLabel>
										<FieldLabel className="col-span-12">
											<Field orientation="horizontal">
												<FieldContent>
													<FieldTitle>Post-roll Ads</FieldTitle>
													<FieldDescription>
														Post-roll ads are played after the episode is completed.
													</FieldDescription>
												</FieldContent>
												<Switch
													disabled={!isEpisodeEditable}
													checked={isPostRollEnabled(
														field.state.value ?? MonetizationStatus.Undefined,
													)}
													onCheckedChange={() =>
														field.handleChange(
															toggleMonetizationStatus(
																field.state.value ?? MonetizationStatus.Undefined,
																"postroll",
															),
														)
													}
												/>
											</Field>
										</FieldLabel>
									</FieldGroup>
								)}
							/>
						</FormCardContent>
					</FormCard>
					<FormCard
						className="xl:col-span-6"
						disabled={!isEpisodeEditable || updateMutation.isPending}
					>
						<FormCardHeader>
							<FormCardTitle>Ad break Jingles</FormCardTitle>
						</FormCardHeader>
						<FormCardContent>
							<ComingSoon />
						</FormCardContent>
					</FormCard>
					<FormCard
						className="xl:col-span-6"
						disabled={!isEpisodeEditable || updateMutation.isPending}
					>
						<FormCardHeader>
							<FormCardTitle>Episode Jingles</FormCardTitle>
						</FormCardHeader>
						<FormCardContent>
							<ComingSoon />
						</FormCardContent>
					</FormCard>
					{isEpisodeEditable && <form.FormSaveBar />}
				</div>
				{isEpisodeEditable && <form.FormBottomBar />}
			</form>
		</form.AppForm>
	);
}
